import React, { useEffect, useRef, useState } from "react";
import "./SenderWaitlist.css";
import "./FlightDetails.css"; /* for .fd-btn family */
import RequestTimeline, { timelineStepFor } from "./RequestTimeline";
import StepIndicator from "./StepIndicator";
import { doc, onSnapshot, collection, getDocs, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { pickWaitlistDoc } from "../utils/liveRequest";
import { statusStamp } from "../utils/status";
import { showToast } from "./Toast";
import { WaitlistSkeleton } from "./Skeleton";
import ConfirmModal from "./ConfirmModal";
import { useNavigate, useLocation } from "react-router-dom";

/* ── Inline SVG icon set (matches FlightDetails) ── */
const Icon = {
  doc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/>
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 1 1 8 0v4"/>
    </svg>
  ),
  package: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  handshake: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 17l-2 2-3-3 5-5 4 4"/><path d="M14 11l3-3 4 4-5 5-2-2"/><path d="M3 14l3-3"/><path d="M18 10l3-3"/>
    </svg>
  ),
  key: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  copy: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  plane: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21 4 19 2c-2-2-4-2-5.5-.5L10 5 1.8 6.2c-.8.2-1.3.8-1.1 1.7l2.7 4.5c.3.5 1 .6 1.5.2L7.5 10l3.5 8.5c.2.5.8.8 1.3.6l4.5-2c.7-.2.8-.9 1-1.9z"/>
    </svg>
  ),
  planeFilled: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M2.5 19h19v2h-19zM22.07 9.64c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 2.59 4.49.74 1.28 1.56-.42 5.18-1.39 4.32-1.16 5.58-1.5c.81-.22 1.28-1.05 1.06-1.84z"/>
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
};

const STATUS_UI = {
  NEW_ORDER: { text: <>Your request has been received. Our team will manually look for a verified traveller on your route.</>, icon: "doc",       step: 3 },
  SEARCHING: { text: <><strong>Our team is looking for a traveller</strong> on your route. We'll update you by SMS soon.</>, icon: "search",   step: 4 },
  MATCHED:   { text: <><strong>Match confirmed.</strong> Our team will share traveller details with you by SMS shortly.</>,                 icon: "check",    step: 6 },
  IN_PROGRESS: { text: <><strong>Your item is in transit.</strong> The traveller is carrying it on their flight.</>,                 icon: "plane",    step: 7 },
  COMPLETED: { text: <><strong>Delivery completed.</strong> Thank you for trying TurantX during the pilot.</>,                    icon: "check",    step: 8 },
};

export default function SenderWaitlist() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("SEARCHING");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [opsReviewed, setOpsReviewed] = useState(false);
  const [activeTab, setActiveTab] = useState("STATUS");
  const [faqOpen, setFaqOpen] = useState(null);
  const [faqQuestion, setFaqQuestion] = useState("");

  // OTP state
  const [confirmedTraveler, setConfirmedTraveler] = useState(null);
  const [senderDocRef, setSenderDocRef] = useState(null);
  const [firstMileOTP, setFirstMileOTP] = useState(null);
  const [lastMileOTP, setLastMileOTP] = useState(null);
  const [lastMileCopied, setLastMileCopied] = useState(false);

  // Traveler details (fetched after match)
  const [travelerDetails, setTravelerDetails] = useState(null);

  // No-match action loading
  const [actionLoading, setActionLoading] = useState(false);

  // Confirm modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Slider state
  const [sliderX, setSliderX] = useState(0);
  const [sliderDone, setSliderDone] = useState(false);
  const sliderTrackRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const THUMB = 56;
  const THRESHOLD = 0.78;

  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const otpRefs = useRef([]);

  const otpComplete = otpDigits.join("").length === 6;

  const getMax = () => {
    if (!sliderTrackRef.current) return 200;
    return sliderTrackRef.current.offsetWidth - THUMB;
  };
  const onDragStart = (clientX) => {
    if (sliderDone || !otpComplete) return;
    isDragging.current = true;
    startX.current = clientX - sliderX;
  };
  const onDragMove = (clientX) => {
    if (!isDragging.current) return;
    const next = Math.min(Math.max(0, clientX - startX.current), getMax());
    setSliderX(next);
  };
  const onDragEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (sliderX / getMax() >= THRESHOLD) {
      setSliderX(getMax());
      setShowConfirmModal(true);
    } else {
      setSliderX(0);
    }
  };
  const onSliderKeyDown = (e) => {
    if (sliderDone || !otpComplete) return;
    const max = getMax();
    const step = Math.max(20, max * 0.1);
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setSliderX((x) => Math.min(max, x + step));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setSliderX((x) => Math.max(0, x - step));
    } else if (e.key === "Home") {
      e.preventDefault();
      setSliderX(0);
    } else if (e.key === "End" || e.key === " " || e.key === "Enter") {
      e.preventDefault();
      setSliderX(max);
      setShowConfirmModal(true);
    }
  };

  const handleLastMileCopy = () => {
    if (!lastMileOTP) return;
    navigator.clipboard.writeText(String(lastMileOTP));
    setLastMileCopied(true);
    if (typeof navigator.vibrate === "function") navigator.vibrate(15);
    setTimeout(() => setLastMileCopied(false), 2000);
  };

  // Pull-to-refresh
  const [refreshing, setRefreshing] = useState(false);
  const pullStartY = useRef(0);
  const isPulling = useRef(false);
  const handlePullStart = (e) => {
    if (window.scrollY === 0) {
      pullStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  };
  const handlePullEnd = (e) => {
    if (!isPulling.current) return;
    isPulling.current = false;
    const pullDistance = (e.changedTouches?.[0]?.clientY || 0) - pullStartY.current;
    if (pullDistance > 80) {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), 1500);
    }
  };

  const handleOtpChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otpDigits];
    next[idx] = val;
    setOtpDigits(next);
    setOtpError("");
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
  };
  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otpDigits[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
  };
  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtpDigits(pasted.split(""));
      otpRefs.current[5]?.focus();
    }
  };

  const confirmHandover = async () => {
    const entered = otpDigits.join("");
    if (entered.length < 6) { setOtpError("Please enter all 6 digits"); return false; }
    if (!firstMileOTP) { setOtpError("OTP not available. Contact support."); return false; }
    if (String(firstMileOTP) !== entered) {
      setOtpError("Incorrect OTP. Please check with your traveller.");
      return false;
    }
    setOtpLoading(true);
    try {
      const ts = new Date().toISOString();
      await updateDoc(senderDocRef, statusStamp("IN_PROGRESS", ts));
      // Keep the matched traveller in sync. The confirmedTraveler entry written
      // by admin uses `phone` (not `phoneNumber`) — using the wrong key here
      // silently skipped this, leaving the traveller stuck on MATCHED.
      if (confirmedTraveler?.phone && confirmedTraveler?.requestKey) {
        const travRef = doc(db, "users", confirmedTraveler.phone, "TravelerRequests", confirmedTraveler.requestKey);
        await updateDoc(travRef, statusStamp("IN_PROGRESS", ts));
      }
      setOtpSuccess(true);
      setSliderDone(true);
      setStatus("IN_PROGRESS");
      showToast("Handover confirmed! Item is now in transit.", "success");
      return true;
    } catch (err) {
      setOtpError("Something went wrong. Please try again.");
      setSliderX(0);
      showToast("Failed to confirm handover. Please try again.", "error");
      return false;
    } finally {
      setOtpLoading(false);
      setShowConfirmModal(false);
    }
  };

  /* ── Helpers ── */
  const getETA = (s) => {
    if (s === "MATCHED" || s === "IN_PROGRESS" || s === "COMPLETED") return "Matched";
    if (s === "SEARCHING") return "Looking for a traveller";
    return "Manual review";
  };
  const getStep = () => STATUS_UI[status]?.step || 3;
  const getStepLabel = () => {
    switch (status) {
      case "COMPLETED":   return "Delivery complete";
      case "IN_PROGRESS": return "Item in transit";
      case "MATCHED":     return "Match confirmed";
      case "SEARCHING":   return "Looking for match";
      default:            return "Request received";
    }
  };

  /* ── Firestore ── */
  useEffect(() => {
    const phone = location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER");
    const role = location.state?.role || localStorage.getItem("USER_ROLE");
    if (!phone || (role && role !== "SENDER")) {
      navigate("/login", { replace: true });
      return;
    }

    let unsub = null;
    const startListening = async () => {
      // The two reads are independent, so they go out together — chaining them
      // cost an extra round trip on the screen the user lands on straight after
      // OTP, before the listener below could even be attached.
      const [reqsSnap, userSnap] = await Promise.all([
        getDocs(collection(db, "users", phone, "SenderRequests")),
        // Tolerated failure: the picker falls back to createdAt ordering.
        getDoc(doc(db, "users", phone)).catch(() => null),
      ]);
      let activeRef = null;
      if (!reqsSnap.empty) {
        // The request to display = live one if any, else the user's latest
        // (latestRequestKey). Without this, a random sibling (e.g. an old
        // CANCELLED request) could win by document-ID order.
        const latestRequestKey =
          userSnap && userSnap.exists() ? userSnap.data().latestRequestKey || null : null;
        const activeDoc = pickWaitlistDoc(reqsSnap.docs, latestRequestKey);
        if (activeDoc) activeRef = doc(db, "users", phone, "SenderRequests", activeDoc.id);
      }
      if (!activeRef) activeRef = doc(db, "users", phone, "Sender", "details");
      setSenderDocRef(activeRef);

      unsub = onSnapshot(activeRef, (snap) => {
        if (!snap.exists()) {
          setStatus("SEARCHING");
          setLoading(false);
          return;
        }
        const data = snap.data();
        setStatus(data.status || (data.requestStatus === "MATCH_FOUND" ? "MATCHED" : data.requestStatus) || "NEW_ORDER");
        setOpsReviewed(!!data.opsReviewed);
        const ct = data.confirmedTraveler || null;
        setConfirmedTraveler(ct);
        if (ct?.phone && ct?.requestKey) {
          getDoc(doc(db, "users", ct.phone, "TravelerRequests", ct.requestKey))
            .then((s) => { if (s.exists()) setTravelerDetails({ phone: ct.phone, ...s.data() }); })
            .catch(() => {});
        }
        setFirstMileOTP(data.firstMileOTP || null);
        setLastMileOTP(data.lastMileOTP || null);
        setSummary({
          requestId: data.uniqueKey,
          fromCity: data.from?.city,
          toCity: data.to?.city,
          itemName: data.itemDetails?.itemName,
          weight: data.itemDetails?.totalWeight,
          deliveryType: data.itemDetails?.deliveryOption === "SELF_DROP_PICK" ? "Self drop & pick" : "Auto drop & pick",
          statusTimestamps: data.statusTimestamps || {},
          createdAt: data.createdAt || null,
          isFirstDelivery: !!data.isFirstDelivery,
        });
        setLoading(false);
      });
    };

    startListening();
    return () => { if (unsub) unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (loading) {
    return (
      <div className="waitlist-page">
        <div className="waitlist-card">
          <WaitlistSkeleton />
        </div>
      </div>
    );
  }

  const sliderPct = Math.round(sliderX / Math.max(1, getMax()) * 100);
  const statusUI = STATUS_UI[status];
  const statusIcon = statusUI ? Icon[statusUI.icon] : null;

  const keepLooking = async () => {
    if (!senderDocRef) return;
    setActionLoading(true);
    try {
      await updateDoc(senderDocRef, statusStamp("SEARCHING"));
      setStatus("SEARCHING");
      showToast("We'll keep looking for a traveller.", "success");
    } catch {
      showToast("Something went wrong. Try again.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const cancelAndNew = async () => {
    if (senderDocRef) {
      setActionLoading(true);
      try {
        const ts = new Date().toISOString();
        await updateDoc(senderDocRef, { ...statusStamp("CANCELLED", ts), cancelledAt: ts });
      } catch { /* navigate anyway */ }
      setActionLoading(false);
    }
    navigate("/selection", { state: { phoneNumber: location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER") } });
  };

  if (status === "NO_MATCH") {
    return (
      <div className="waitlist-page">
        <div className="waitlist-card" style={{ textAlign: "center", padding: "40px 24px" }}>
          <div style={{ width: 72, height: 72, borderRadius: 20, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: "2rem" }}>
            🔍
          </div>
          <h2 style={{ fontSize: "1.3rem", fontWeight: 800, color: "#1a1a2e", marginBottom: 8 }}>No traveller found yet</h2>
          <p style={{ color: "#667781", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: 8, maxWidth: 340, margin: "0 auto 8px" }}>
            We searched but couldn't find a verified traveller on your route
            {summary?.fromCity && summary?.toCity ? <> (<strong>{summary.fromCity} → {summary.toCity}</strong>)</> : ""} yet.
          </p>
          <p style={{ color: "#667781", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: 28, maxWidth: 340, margin: "0 auto 28px" }}>
            Want us to keep looking, or cancel and start fresh?
          </p>
          <button
            type="button"
            className="fd-btn fd-btn--primary fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto 12px" }}
            onClick={keepLooking}
            disabled={actionLoading}
          >
            {actionLoading ? "Please wait…" : "Keep looking"}
          </button>
          <button
            type="button"
            className="fd-btn fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto 16px", background: "#fff", color: "#dc2626", border: "1.5px solid #fca5a5" }}
            onClick={cancelAndNew}
            disabled={actionLoading}
          >
            Cancel &amp; start new request
          </button>
          <button
            type="button"
            className="fd-btn fd-btn--ghost fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            onClick={() => window.dispatchEvent(new CustomEvent("tx-chat-open", { detail: { prefill: "Hi TurantX, no traveller was found for my request. Can you help?" } }))}
          >
            💬 Chat with support
          </button>
        </div>
      </div>
    );
  }

  if (status === "CANCELLED") {
    return (
      <div className="waitlist-page">
        <div className="waitlist-card" style={{ textAlign: "center", padding: "40px 24px" }}>
          <div style={{ width: 72, height: 72, borderRadius: 20, background: "#fff2f2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: "2rem" }}>
            ❌
          </div>
          <h2 style={{ fontSize: "1.3rem", fontWeight: 800, color: "#1a1a2e", marginBottom: 8 }}>Request Cancelled</h2>
          <p style={{ color: "#667781", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: 28, maxWidth: 320, margin: "0 auto 28px" }}>
            Your delivery request has been cancelled. If this was a mistake, please reach out to us on TurantChat.
          </p>
          <button
            type="button"
            className="fd-btn fd-btn--primary fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto 16px" }}
            onClick={() => navigate("/selection", { state: { phoneNumber: location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER") } })}
          >
            <span style={{ marginRight: 6 }}>+</span> Start new request
          </button>
          <button
            type="button"
            className="fd-btn fd-btn--ghost fd-btn--block"
            style={{ maxWidth: 320, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            onClick={() => window.dispatchEvent(new CustomEvent("tx-chat-open", { detail: { prefill: "Hi TurantX, my request was cancelled. Can you help?" } }))}
          >
            💬 Chat with support
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="waitlist-page" onTouchStart={handlePullStart} onTouchEnd={handlePullEnd}>
      {refreshing && (
        <div className="pull-refresh-indicator">
          <div className="refresh-spinner" />
          <span>Refreshing…</span>
        </div>
      )}

      <div className="waitlist-card">
        <StepIndicator current={getStep()} total={8} label={getStepLabel()} />

        <h2 className="wl-title">
          <span className="wl-title-icon" aria-hidden>{Icon.doc}</span>
          Thanks! Your request is in.
        </h2>
        <p className="wl-subtitle">
          Our team is manually looking for a verified traveller on your route. We'll keep you updated via SMS and TurantChat.
        </p>

        {summary?.isFirstDelivery ? (
          <div className="wl-first-free-badge">🎁 This delivery is FREE — your first with TurantX</div>
        ) : (
          <div className="wl-fee-badge">A delivery fee may apply from your 2nd delivery — our team will share the amount before it's arranged</div>
        )}

        <button
          type="button"
          className="wl-dashboard-btn"
          onClick={() => navigate("/dashboard", { state: { phoneNumber: location.state?.phoneNumber || localStorage.getItem("PHONE_NUMBER") } })}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="wl-dashboard-btn-icon">
            <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
          </svg>
          My Dashboard
        </button>

        {/* ---------- TABS ---------- */}
        <div className="tabs" role="tablist" aria-label="Waitlist sections">
          {[
            ["STATUS", "Status"],
            ["REQUEST", "Your Request"],
            ...(status === "MATCHED" || status === "IN_PROGRESS" ? [["TRAVELLER", "Your Traveller"]] : []),
            ["PAYMENT", "Payment"],
            ["FAQ", "FAQ"],
          ].map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={activeTab === key}
              className={activeTab === key ? "tab active" : "tab"}
              onClick={() => setActiveTab(key)}
            >{label}</button>
          ))}
        </div>

        {/* ---------- STATUS TAB ---------- */}
        {activeTab === "STATUS" && (
          <div className="wl-panel" role="tabpanel">
            {opsReviewed && (
              <div className="ops-badge">
                <span className="dot-green" aria-hidden></span>
                Reviewed by TurantX Operations
              </div>
            )}

            <div className="eta-card">
              <span className="eta-card-icon" aria-hidden>{Icon.clock}</span>
              Estimated matching time:&nbsp;<strong>{getETA(status)}</strong>
            </div>

            {statusUI && (
              <div className="status-banner">
                <span className="status-banner-icon" aria-hidden>{statusIcon}</span>
                <div className="status-banner-text">{statusUI.text}</div>
              </div>
            )}

            {/* ---- HANDOVER OTP ---- */}
            {!otpSuccess && status !== "IN_PROGRESS" && status !== "COMPLETED" && (
              <div className={`otp-card ${status === "MATCHED" ? "" : "is-locked"}`}>
                <div className={`otp-card-badge ${status === "MATCHED" ? "" : "is-muted"}`}>
                  {status === "MATCHED" ? "Action required" : "Step 2 · Coming soon"}
                </div>
                <div className="otp-card-icon-wrap" aria-hidden>
                  {status === "MATCHED" ? Icon.handshake : Icon.lock}
                </div>
                <h3 className="otp-card-title">
                  {status === "MATCHED" ? "Confirm item handover" : "Handover via OTP"}
                </h3>
                <p className="otp-card-subtitle">
                  {status === "MATCHED"
                    ? <>Ask your traveller to show their <strong>6-digit OTP</strong> and enter it below to confirm handover.</>
                    : <>When your traveller is matched, you'll <strong>get an OTP from them</strong> and enter it here to confirm the handover.</>}
                </p>

                <div className="handover-steps">
                  <div className="handover-step">
                    <span className="step-num">1</span>
                    <span>Hand your package to the verified traveller.</span>
                  </div>
                  <div className="handover-step">
                    <span className="step-num">2</span>
                    <span>Verify the <b>handover OTP</b> displayed in their TurantX app.</span>
                  </div>
                  <div className="handover-step">
                    <span className="step-num">3</span>
                    <span>Enter the <b>6-digit OTP</b> to complete the handover securely.</span>
                  </div>
                </div>

                <div className="otp-input-row" onPaste={status === "MATCHED" ? handleOtpPaste : undefined}>
                  {otpDigits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      className={`otp-box clarity-mask ${status !== "MATCHED" ? "is-locked" : ""}`}
                      type="tel"
                      inputMode="numeric"
                      autoComplete={i === 0 && status === "MATCHED" ? "one-time-code" : "off"}
                      maxLength={1}
                      value={status === "MATCHED" ? d : ""}
                      placeholder={status === "MATCHED" ? "" : "•"}
                      readOnly={status !== "MATCHED"}
                      aria-label={`Handover OTP digit ${i + 1}`}
                      onChange={(e) => status === "MATCHED" && handleOtpChange(e.target.value, i)}
                      onKeyDown={(e) => status === "MATCHED" && handleOtpKeyDown(e, i)}
                    />
                  ))}
                </div>

                {otpError && <p className="otp-error" role="alert">{otpError}</p>}

                {status === "MATCHED" ? (
                  <>
                    <div
                      ref={sliderTrackRef}
                      className={`slider-track ${!otpComplete ? "slider-disabled" : ""} ${sliderDone ? "slider-done" : ""}`}
                      onMouseMove={(e) => onDragMove(e.clientX)}
                      onMouseUp={onDragEnd}
                      onMouseLeave={onDragEnd}
                      onTouchMove={(e) => onDragMove(e.touches[0].clientX)}
                      onTouchEnd={onDragEnd}
                      role="slider"
                      aria-label="Slide to confirm handover"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={sliderPct}
                      aria-disabled={!otpComplete || sliderDone}
                      tabIndex={otpComplete && !sliderDone ? 0 : -1}
                      onKeyDown={onSliderKeyDown}
                    >
                      <div className="runway-dashes" />
                      <div className="slider-fill" style={{ width: sliderX + 60 }} />
                      <span className="slider-label">
                        {sliderDone
                          ? (otpLoading ? "Verifying…" : "Dispatched")
                          : !otpComplete
                            ? "Enter OTP to enable"
                            : (
                              <>
                                Slide to confirm handover
                                <span className="slider-label-icon" aria-hidden>{Icon.arrowRight}</span>
                              </>
                            )}
                      </span>
                      <div
                        className="slider-thumb"
                        style={{ transform: `translateX(${sliderX}px)` }}
                        onMouseDown={(e) => { e.preventDefault(); onDragStart(e.clientX); }}
                        onTouchStart={(e) => onDragStart(e.touches[0].clientX)}
                        aria-hidden
                      >
                        <span className="slider-thumb-icon" aria-hidden>{sliderDone ? Icon.check : Icon.planeFilled}</span>
                      </div>
                    </div>
                    <p className="slider-fallback">
                      Or{" "}
                      <button
                        type="button"
                        className="slider-fallback-btn"
                        disabled={!otpComplete || sliderDone}
                        onClick={() => setShowConfirmModal(true)}
                      >tap here to confirm</button>
                    </p>
                    <p className="otp-card-warn">
                      <span className="otp-card-warn-icon" aria-hidden>{Icon.alert}</span>
                      Only enter the OTP shown on your traveller's screen.
                    </p>
                  </>
                ) : (
                  <button
                    type="button"
                    className="fd-btn fd-btn--primary fd-btn--block otp-card-cta"
                    disabled
                  >
                    <span className="fd-btn-icon" aria-hidden>{Icon.lock}</span>
                    Waiting for match
                  </button>
                )}
              </div>
            )}

            {(otpSuccess || status === "IN_PROGRESS" || status === "COMPLETED") && (
              <div className="success-card">
                <div className="success-card-icon" aria-hidden>{Icon.check}</div>
                <h3>{status === "COMPLETED" ? "Delivered" : "Handover confirmed"}</h3>
                <p>
                  {status === "COMPLETED"
                    ? "Your document has been delivered successfully."
                    : "Your item is now with the traveller. We'll keep you updated."}
                </p>
              </div>
            )}

            {/* ---- LAST MILE OTP — show when IN_PROGRESS ---- */}
            {status === "IN_PROGRESS" && (
              <div className="otp-card">
                <div className="otp-card-badge">Share with receiver</div>
                <div className="otp-card-icon-wrap" aria-hidden>{Icon.key}</div>
                <h3 className="otp-card-title">Delivery OTP</h3>
                <p className="otp-card-subtitle">
                  Share this OTP with your <strong>receiver</strong>. They'll give it to the traveller when the item is delivered.
                </p>
                <div className="otp-digits-row">
                  {String(lastMileOTP || "------").split("").map((d, i) => (
                    <div key={i} className="otp-digit">{d}</div>
                  ))}
                </div>
                <button
                  type="button"
                  className="fd-btn fd-btn--primary fd-btn--block otp-card-cta"
                  onClick={handleLastMileCopy}
                >
                  <span className="fd-btn-icon" aria-hidden>{lastMileCopied ? Icon.check : Icon.copy}</span>
                  {lastMileCopied ? "Copied!" : "Copy OTP"}
                </button>
                <p className="otp-card-warn">
                  <span className="otp-card-warn-icon" aria-hidden>{Icon.shield}</span>
                  Share only with your receiver or trusted contact.
                </p>
              </div>
            )}

            <RequestTimeline currentStep={timelineStepFor(status)} userType="SENDER" stamps={summary?.statusTimestamps} createdAt={summary?.createdAt} />

            <div className="trust-strip is-info">
              <span className="trust-strip-icon" aria-hidden>{Icon.chat}</span>
              <div className="trust-strip-text">
                <strong>Need help?</strong>
                <p>Use the 💬 chat button at the bottom right — we usually respond within an hour.</p>
              </div>
            </div>

            <div className="waitlist-note">Matches are subject to availability.</div>
          </div>
        )}

        {/* ---------- REQUEST TAB ---------- */}
        {activeTab === "REQUEST" && summary && (
          <div className="wl-panel" role="tabpanel">
            <div className="request-summary-card">
              <div className="summary-row">
                <span className="label">Request ID</span>
                <span className="value">TX-{summary.requestId}</span>
              </div>
              <div className="summary-row">
                <span className="label">Route</span>
                <span className="value">{summary.fromCity} → {summary.toCity}</span>
              </div>
              <div className="summary-row">
                <span className="label">Item</span>
                <span className="value">{summary.itemName}</span>
              </div>
              <div className="summary-row">
                <span className="label">Weight</span>
                <span className="value">{summary.weight}</span>
              </div>
              <div className="summary-row">
                <span className="label">Delivery</span>
                <span className="value">{summary.deliveryType}</span>
              </div>
            </div>
          </div>
        )}

        {/* ---------- TRAVELLER TAB ---------- */}
        {activeTab === "TRAVELLER" && (
          <div className="wl-panel" role="tabpanel">
            {(() => {
              const fd = travelerDetails?.flightDetails || confirmedTraveler?.flightDetails || null;
              const phone = travelerDetails?.phone || confirmedTraveler?.phone || null;
              // Show the exact place the traveller picked from autocomplete
              // (fullAddress), not just the derived city. Fall back to city only
              // if an older doc has no fullAddress.
              const fromAddr = travelerDetails?.from?.fullAddress || travelerDetails?.from?.city || null;
              const toAddr   = travelerDetails?.to?.fullAddress   || travelerDetails?.to?.city   || null;
              const mapsUrl = (loc, label) => {
                if (loc?.latitude && loc?.longitude)
                  return `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`;
                if (label) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;
                return null;
              };
              const fromUrl = mapsUrl(travelerDetails?.from, fromAddr);
              const toUrl   = mapsUrl(travelerDetails?.to,   toAddr);
              const airline  = fd?.airline === "Other" ? (fd?.customAirline || "Other") : fd?.airline;
              const travelDate = fd?.travelDate || null;

              if (!fd && !phone) return (
                <div className="trust-strip is-info" style={{ marginTop: 8 }}>
                  <span className="trust-strip-icon" aria-hidden>{Icon.clock}</span>
                  <div className="trust-strip-text">
                    <strong>Traveller details</strong>
                    <p>Loading traveller details… If this persists, our team will share the details via SMS or TurantChat.</p>
                  </div>
                </div>
              );

              return (
                <div className="request-summary-card">
                  {(fd?.firstName || fd?.lastName) && (
                    <div className="summary-row">
                      <span className="label">Traveller name</span>
                      <span className="value">{[fd.firstName, fd.lastName].filter(Boolean).join(" ")}</span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span className="label">PNR verified</span>
                    <span className="value" style={{ fontWeight: 700, color: travelerDetails?.flightDetailsVerified ? "#0f9d58" : "#f59e0b" }}>
                      {travelerDetails?.flightDetailsVerified ? "✓ Verified" : "⏳ Pending"}
                    </span>
                  </div>
                  {phone && (
                    <div className="summary-row">
                      <span className="label">Phone</span>
                      <span className="value">
                        <a href={`tel:+91${phone}`} style={{ color: "var(--color-primary, #FF621A)", fontWeight: 600, textDecoration: "none" }}>
                          +91 {phone}
                        </a>
                      </span>
                    </div>
                  )}
                  {(fromAddr || toAddr) && (
                    <div className="summary-row">
                      <span className="label">Route</span>
                      <span className="value" style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                        {fromUrl
                          ? <a href={fromUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-primary, #FF621A)", fontWeight: 600, textDecoration: "none" }}>{fromAddr}</a>
                          : <span>{fromAddr || "—"}</span>}
                        <span>→</span>
                        {toUrl
                          ? <a href={toUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-primary, #FF621A)", fontWeight: 600, textDecoration: "none" }}>{toAddr}</a>
                          : <span>{toAddr || "—"}</span>}
                      </span>
                    </div>
                  )}
                  {airline && (
                    <div className="summary-row">
                      <span className="label">Airline</span>
                      <span className="value">{airline}</span>
                    </div>
                  )}
                  {travelDate && (
                    <div className="summary-row">
                      <span className="label">Flight date</span>
                      <span className="value">{travelDate}</span>
                    </div>
                  )}
                  {travelerDetails?.itemReadyForPickup && (
                    <div style={{ marginTop: 12, padding: "10px 14px", background: "#e7f9ef", border: "1px solid #b2dfce", borderRadius: 10 }}>
                      <span style={{ fontWeight: 700, color: "#0f9d58", fontSize: "0.88rem" }}>
                        📦 Item marked ready for pickup
                        {travelerDetails.pickupAvailableFrom
                          ? (() => {
                              const [h, m] = travelerDetails.pickupAvailableFrom.split(":");
                              const hr = +h;
                              return ` · available from ${hr % 12 || 12}:${m} ${hr < 12 ? "AM" : "PM"}`;
                            })()
                          : ""}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className={`trust-strip ${travelerDetails?.flightDetailsVerified ? "is-success" : "is-info"}`} style={{ marginTop: 12 }}>
              <span className="trust-strip-icon" aria-hidden>
                {travelerDetails?.flightDetailsVerified ? Icon.check : Icon.shield}
              </span>
              <div className="trust-strip-text">
                {travelerDetails?.flightDetailsVerified ? (
                  <>
                    <strong>PNR verified ✓</strong>
                    <p>This traveller's flight PNR has been verified by TurantX operations.</p>
                  </>
                ) : (
                  <>
                    <strong>Verification in progress</strong>
                    <p>TurantX is verifying this traveller's flight details — this'll update automatically once it's done.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ---------- PAYMENT TAB ---------- */}
        {activeTab === "PAYMENT" && (
          <div className="wl-panel" role="tabpanel">
            <div className="trust-strip is-success">
              <span className="trust-strip-icon" aria-hidden>{Icon.shield}</span>
              <div className="trust-strip-text">
                <strong>Payment</strong>
                <p><b>No payment is taken in the app.</b> If a delivery fee applies, our team shares the amount before the delivery is arranged.</p>
              </div>
            </div>
            <div className="trust-strip is-info">
              <span className="trust-strip-icon" aria-hidden>{Icon.chat}</span>
              <div className="trust-strip-text">
                <strong>Official contact only</strong>
                <p>We will only contact you by SMS from TRANTX or here on TurantChat. Please ignore messages from personal numbers.</p>
              </div>
            </div>
            <div className="invoice-card">
              <h4>Invoice &amp; receipt</h4>
              <p>No payment has been collected for this request. Once in-app payments are enabled, your invoices will appear here.</p>
              <button
                type="button"
                className="fd-btn fd-btn--ghost fd-btn--block"
                disabled
              >
                Download invoice (coming soon)
              </button>
            </div>
          </div>
        )}

        {/* ---------- FAQ TAB ---------- */}
        {activeTab === "FAQ" && (
          <div className="wl-panel" role="tabpanel">
            <div className="faq-list">
              {[
                {
                  q: "How long will it take to find a traveller?",
                  a: "Every request is reviewed by our team before matching begins. As soon as a verified traveller is confirmed on your route, we'll notify you immediately via SMS and your dashboard.",
                },
                {
                  q: "What if no traveller is found?",
                  a: "If we can't find a verified traveller for your route, we'll notify you via SMS and your dashboard. Nothing is charged — your request simply closes.",
                },
                {
                  q: "Can I change my item details after submitting?",
                  a: "Yes. Contact our support team through TurantChat before a traveller is confirmed, and we'll update your request.",
                },
                {
                  q: "How do I know I'm handing it to the right traveller?",
                  a: "Before handover, check the traveller's boarding pass—the name will match the verified traveller details shared by TurantX. Then confirm the handover using your OTP. Before boarding, airlines and airport security also verify the traveller's identity against their boarding pass as part of the normal airport process.",
                },
                {
                  q: "What if my item is lost or damaged?",
                  a: "During the pilot, our team personally coordinates every delivery and stays in touch with both parties until the document reaches your receiver. If anything goes wrong, contact us through TurantChat immediately. We retain the traveller's verified flight and contact details, actively assist in tracing your document, and work with both parties until the issue is resolved.",
                },
                {
                  q: "Can I cancel my request?",
                  a: "Yes. Contact our support team through TurantChat. If a traveller has already been matched, please let us know as early as possible so we can coordinate with them.",
                },
                {
                  q: "Is my phone number shared with the traveller?",
                  a: "Only after a match is confirmed, and only when it's necessary to coordinate pickup or delivery. We never share your contact details with anyone else.",
                },
                {
                  q: "Why do I need to verify the handover using an OTP?",
                  a: "The Handover OTP confirms that your document has been given to the correct traveller. Combined with the boarding pass verification, it helps ensure the right person carries your document from the very first step.",
                },
                {
                  q: "How will I know when to collect my item?",
                  a: "We'll keep you updated throughout the delivery journey. Once your traveller arrives at the destination and marks your item as ready for pickup, we'll notify you immediately.",
                },
                {
                  q: "How is the delivery completed?",
                  a: "When your receiver collects the document, they simply share the Delivery OTP shown in your TurantX app with the traveller. Once it's verified, the delivery is completed and confirmed.",
                },
              ].map((item, i) => (
                <div key={i} className="faq-item">
                  <button
                    type="button"
                    className={`faq-question${faqOpen === i ? " is-open" : ""}`}
                    onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                    aria-expanded={faqOpen === i}
                  >
                    <span>{item.q}</span>
                    <svg
                      className="faq-chevron"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      {faqOpen === i
                        ? <polyline points="18 15 12 9 6 15" />
                        : <polyline points="6 9 12 15 18 9" />}
                    </svg>
                  </button>
                  {faqOpen === i && (
                    <p className="faq-answer">{item.a}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="faq-ask">
              <p className="faq-ask-label">Don't see your question?</p>
              <textarea
                className="faq-ask-input"
                placeholder="Type your question here…"
                rows={3}
                value={faqQuestion}
                onChange={(e) => setFaqQuestion(e.target.value)}
              />
              <button
                type="button"
                className={`fd-btn fd-btn--primary fd-btn--block faq-ask-btn${!faqQuestion.trim() ? " faq-ask-btn--disabled" : ""}`}
                onClick={() => {
                  if (!faqQuestion.trim()) return;
                  window.dispatchEvent(new CustomEvent("tx-chat-open", { detail: { prefill: faqQuestion.trim() } }));
                  setFaqQuestion("");
                }}
                aria-disabled={!faqQuestion.trim()}
              >
                💬 Send on TurantChat →
              </button>
            </div>
          </div>
        )}
      </div>

      {showConfirmModal && (
        <ConfirmModal
          title="Confirm Handover"
          message="Are you sure you want to hand over the item to this traveller? This action cannot be undone."
          confirmText="Yes, confirm handover"
          cancelText="Go back"
          loading={otpLoading}
          variant="primary"
          onConfirm={confirmHandover}
          onCancel={() => { setShowConfirmModal(false); setSliderX(0); }}
        />
      )}
    </div>
  );
}
